import { Component, effect, inject, input } from '@angular/core';
import { Observable, map } from 'rxjs';

import { EChartsOption } from 'echarts';
import { NgxEchartsDirective, provideEcharts } from 'ngx-echarts';

import { RegisterComponent } from '../../components/component-lookup';
import { GlobalConstants } from '../../../shared/global-constants';
import { VisualBaseComponent } from '../visual-base.component';
import { Peer, StateService, SubjectType } from '../../services/state.service';
import { ChartInfoBoxComponent } from '../chart-info-box/chart-info-box.component';

interface MetricData {
  name: string,
  value: number,
  itemStyle: {
    color: string
  }
}

@RegisterComponent('BarHorizontalChart')
@Component({
  selector: 'app-bar-horizontal-chart',
  standalone: true,
  imports: [NgxEchartsDirective, ChartInfoBoxComponent],
  templateUrl: './bar-horizontal-chart.component.html',
  styleUrl: './bar-horizontal-chart.component.scss',
  providers: [provideEcharts()]
})
export class BarHorizontalChartComponent extends VisualBaseComponent {
  elementId = input.required<string>();
  subjectType = input.required<SubjectType>();

  readonly institution = inject(StateService).institution;
  readonly peer = inject(StateService).peer;
  readonly filteredGroup = inject(StateService).filteredGroup;
  readonly dateRange = inject(StateService).dateRange;

  override loadingColor = '#ebebeb';
  override initOptions = { height: 200 }
  override baseOptions: EChartsOption = {
    grid: {
      left: 20,
      right: 30,
      top: 20,
      bottom: 35,
      containLabel: true
    },
    legend: {
      show: true
    },
    xAxis: {
      type: 'value',
      show: true,
      axisLabel: {
        color: '#243b5b',
        formatter: function (value: number) {
          return value.toLocaleString() + '%';
        }
      },
      splitLine: {
        lineStyle: {
          color: '#ccc'
        }
      }
    },
    yAxis: {
      type: 'category',
      show: true,
      data: []
    },
    series: [
      {
        type: 'bar',
        data: []
      }
    ]
  };

  constructor() {
    super();

    effect(() => {
      const subjectType = this.subjectType();
      const peer = subjectType == SubjectType.peer ? this.peer() : undefined;
      const date = this.dateRange()?.endDate;
      const elementId = this.elementId();
      const group = this.filteredGroup();
      if (this.chartInit() && subjectType && (peer || subjectType == SubjectType.institution) && date && elementId && group) {
        this.updateVisual(peer, date, elementId);
      }
    });
  }

  public updateVisual(peer: Peer | undefined, date: Date, elementId: string) {
    this.processMetricData(this.subjectType(), peer, date, elementId).subscribe(metricData => {
      const companionType = this.subjectType() == SubjectType.institution ? SubjectType.peer : SubjectType.institution;
      const companion = this.subjectType() == SubjectType.institution ? this.peer() : undefined;
      this.processMetricData(companionType, companion, date, elementId).subscribe(companionData => {
        const maxValue = this.getMaxValue(metricData, companionData);

        if (metricData.length == 0) {
          this.echartsInstance?.clear();
          this.chartOptions = this.noDataOptions;
        }
        else {
          this.updateOptions = {
            tooltip: {
              trigger: 'item',
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              formatter: function (params: any) {
                return '<div style="margin: 0px 0 0;line-height:1;"><span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color: ' + params.color + ';"></span><span style="font-size:14px;color:#666;font-weight:400;margin-left:2px">' + params.name + '</span><span style="float:right;margin-left:20px;font-size:14px;color:#666;font-weight:900">' + params.value.toLocaleString() + '%</span><div style="clear:both"></div></div>';
              }
            },
            yAxis: {
              data: metricData.map(v => v.name).reverse()
            },
            xAxis: {
              min: 0,
              max: Math.ceil(maxValue * 1.1),
            },
            series: [
              {
                data: metricData.reverse()
              }
            ]
          };
        }
        if (!this.echartsInstance?.isDisposed()) {
          this.echartsInstance?.hideLoading();
        }
      });
    });
  }

  processMetricData(subjectType: SubjectType, peer: Peer | undefined, date: Date, elementId: string): Observable<MetricData[]> {
    return this.metricService.getElementData(this.institution().iid, elementId, peer).pipe(map(metrics => {
      const metricData: MetricData[] = [];
      let index = 0;
      metrics.forEach((metric) => {
        const dataRow = metric.data.findIndex((r) => { return new Date(r[0]).getTime() == date.getTime(); });

        let indexes: number[] = [];
        if (subjectType == SubjectType.peer) {
          indexes = this.visualService.getPeerIndexes(metric.data[0]);
        } else {
          indexes.push(metric.data[0].findIndex((c: number) => {
            return c == this.institution().iid;
          }));
        }

        let sum = 0; let count = 0;
        indexes.forEach(i => {
          if (dataRow > -1 && metric.data[dataRow][i] != null) {
            sum += metric.data[dataRow][i]; count++;
          }
        });
        const value: number = count > 0 ? sum / count : 0;

        const color: string = this.chartColors().find(c => c.metricId == metric.metricId)?.color ?? GlobalConstants.barColors[index];
        metricData[index++] = {
          name: metric.rowTitle, value: value, itemStyle: { color: color }
        };
      });
      return metricData;
    }));
  }

  getMaxValue(metricData: MetricData[], companionData: MetricData[]): number {
    const values = metricData.map(v => v.value).concat(companionData.map(v => v.value));
    return Math.max(...values);
  }
}
